import { useState, useEffect, useMemo } from 'react'
import lucideIcons from '@iconify-json/lucide/icons.json'
import { ensureKebabCase } from '@/utils/base'

// 添加格式化时长的工具函数
const formatDuration = (duration: number): string => {
  const minutes = Math.floor(duration / 60)
  const seconds = Math.floor(duration % 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

// 内存实现
const thumbnailCache = new Map<string, string>()

// 生成缓存key的函数
const getCacheKey = (url: string, options: Record<string, any>) => {
  return `${url}-${JSON.stringify(options)}`
}

export const useIconThumbnail = (
  link?: string,
  options: {
    quality?: number // 图片质量 0-1
    width?: number // 指定输出宽度
    height?: number // 指定输出高度
  } = {},
) => {
  const { width = 64, height = 64 } = options
  const [thumbnail, setThumbnail] = useState<string | undefined>()

  // @ts-ignore
  const selectIcon = lucideIcons.icons?.[ensureKebabCase(link)]

  useEffect(() => {
    if (!selectIcon) return
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('canvas context not exist')
    }

    const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
      ${selectIcon.body}
    </svg>
  `
    // 创建一个 Blob 对象
    const svgBlob = new Blob([svgString], {
      type: 'image/svg+xml;charset=utf-8',
    })
    const url = URL.createObjectURL(svgBlob)

    const img = new Image()
    img.onload = () => {
      ctx.fillStyle = 'white'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 8, 8, canvas.width - 16, canvas.height - 16)
      URL.revokeObjectURL(url) // 释放 URL 对象

      const dataURL = canvas.toDataURL('image/png')
      setThumbnail(dataURL)
    }

    img.src = url

    return () => {
      canvas.remove() // 移除 canvas 元素
    }
  }, [selectIcon, width, height])

  return { thumbnail }
}

export const useVideoThumbnail = (
  videoUrl: string,
  options: {
    quality?: number // 图片质量 0-1
    type?: string // 图片类型，如 'image/jpeg' 或 'image/png'
    width?: number // 指定输出宽度
    height?: number // 指定输出高度
    defaultThumbnail?: string // 默认缩略图
    frameIndex?: number // 指定截取第几帧，默认第一帧
  } = {},
) => {
  const {
    quality = 0.8,
    type = 'image/png',
    width = 200,
    height = 200,
    defaultThumbnail = 'https://heyboss.heeyo.ai/design-system/video_0gPfq4jj.png',
    frameIndex = 1,
  } = options

  const [thumbnail, setThumbnail] = useState<string | null>(() => {
    // 初始化时检查缓存
    const cacheKey = getCacheKey(videoUrl, options)
    return thumbnailCache.get(cacheKey) || defaultThumbnail
  })
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!videoUrl) return

    const cacheKey = getCacheKey(videoUrl, options)
    const cachedThumbnail = thumbnailCache.get(cacheKey)

    if (cachedThumbnail) {
      setThumbnail(cachedThumbnail)
      return
    }

    const video = document.createElement('video')
    const canvas = document.createElement('canvas')

    setLoading(true)

    const handleLoadedData = () => {
      try {
        // 确保视频已经加载完成并可以播放
        video
          .play()
          .then(() => {
            // 暂停视频以便截图
            video.pause()

            // 设置视频时间到指定帧
            if (frameIndex > 0) {
              // 假设视频帧率为 30fps
              video.currentTime = frameIndex / 30
            }

            // 等待视频时间更新完成后再截图
            video.addEventListener(
              'seeked',
              () => {
                // 设置 canvas 尺寸
                const videoWidth = width || video.videoWidth
                const videoHeight = height || video.videoHeight

                canvas.width = videoWidth
                canvas.height = videoHeight

                // 绘制视频帧到 canvas
                const ctx = canvas.getContext('2d')
                if (!ctx) {
                  throw new Error('无法获取 canvas context')
                }

                ctx.drawImage(video, 0, 0, videoWidth, videoHeight)

                // 添加半透明黑色遮罩在底部
                const overlayHeight = 32
                const overlayWidth = 100
                const overlayX = (videoWidth - overlayWidth) / 2
                const overlayY = videoHeight - overlayHeight - 24

                // 使用 fillRect 替代 roundRect 来避免四角黑色问题
                ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
                ctx.beginPath()
                const radius = 20

                // 手动绘制圆角矩形
                ctx.moveTo(overlayX + radius, overlayY)
                ctx.lineTo(overlayX + overlayWidth - radius, overlayY)
                ctx.quadraticCurveTo(
                  overlayX + overlayWidth,
                  overlayY,
                  overlayX + overlayWidth,
                  overlayY + radius,
                )
                ctx.lineTo(
                  overlayX + overlayWidth,
                  overlayY + overlayHeight - radius,
                )
                ctx.quadraticCurveTo(
                  overlayX + overlayWidth,
                  overlayY + overlayHeight,
                  overlayX + overlayWidth - radius,
                  overlayY + overlayHeight,
                )
                ctx.lineTo(overlayX + radius, overlayY + overlayHeight)
                ctx.quadraticCurveTo(
                  overlayX,
                  overlayY + overlayHeight,
                  overlayX,
                  overlayY + overlayHeight - radius,
                )
                ctx.lineTo(overlayX, overlayY + radius)
                ctx.quadraticCurveTo(
                  overlayX,
                  overlayY,
                  overlayX + radius,
                  overlayY,
                )
                ctx.closePath()
                ctx.fill()

                // 绘制视频图标
                ctx.fillStyle = '#FFFFFF'
                ctx.beginPath()
                const iconSize = 16
                const iconX = overlayX + 16
                const iconY = overlayY + overlayHeight / 2

                // 绘制三角形播放图标
                ctx.moveTo(iconX, iconY - iconSize / 2)
                ctx.lineTo(iconX + iconSize, iconY)
                ctx.lineTo(iconX, iconY + iconSize / 2)
                ctx.closePath()
                ctx.fill()

                // 格式化视频时长
                const duration = video.duration
                const durationText = formatDuration(duration)

                // 绘制时长文本
                ctx.fillStyle = '#FFFFFF'
                ctx.font = 'bold 18px Inter'
                ctx.textAlign = 'center'
                ctx.textBaseline = 'middle'
                ctx.fillText(
                  durationText,
                  videoWidth / 2 + 8,
                  overlayY + overlayHeight / 2,
                ) // 文字位置在遮罩中间

                // 转换为 base64
                const dataUrl = canvas.toDataURL(type, quality)
                // 存入缓存
                thumbnailCache.set(cacheKey, dataUrl)
                setThumbnail(dataUrl)
                setLoading(false)
              },
              { once: true },
            )
          })
          .catch((err) => {
            throw err
          })
      } catch (err) {
        setError(err instanceof Error ? err : new Error('生成缩略图失败'))
        setLoading(false)
        if (defaultThumbnail) {
          setThumbnail(defaultThumbnail)
        }
      }
    }

    const handleError = () => {
      setError(new Error('视频加载失败'))
      setLoading(false)
    }

    // 设置视频事件处理
    video.addEventListener('loadeddata', handleLoadedData)
    video.addEventListener('error', handleError)

    // 设置视频源
    video.src = videoUrl
    video.crossOrigin = 'anonymous' // 处理跨域
    video.preload = 'auto' // 改为 auto 以确保完整加载
    video.muted = true // 必须设置 muted 才能调用 play()

    // 清理函数
    return () => {
      video.removeEventListener('loadeddata', handleLoadedData)
      video.removeEventListener('error', handleError)
      video.src = ''
      video.remove() // 移除 video 元素

      canvas.remove() // 移除 canvas 元素
      const ctx = canvas.getContext('2d')
      if (ctx) {
        // @ts-ignore
        ctx.clearRect(0, 0, canvas.width, canvas.height) // 清除 canvas 内容
      }
    }
  }, [
    videoUrl,
    options.quality,
    options.type,
    options.width,
    options.height,
    options.defaultThumbnail,
    options.frameIndex,
  ])

  return { thumbnail, loading, error }
}

export const useAudioThumbnail = (
  audioUrl: string,
  options: {
    quality?: number // 图片质量 0-1
    type?: string // 图片类型，如 'image/jpeg' 或 'image/png'
    defaultThumbnail?: string // 默认缩略图
  } = {},
) => {
  const {
    quality = 1,
    type = 'image/png',
    defaultThumbnail = 'https://heyboss.heeyo.ai/design-system/audio_tPAodmps.png',
  } = options

  const [thumbnail, setThumbnail] = useState<string | null>(() => {
    // 初始化时检查缓存
    const cacheKey = getCacheKey(audioUrl, options)
    return thumbnailCache.get(cacheKey) || defaultThumbnail
  })
  const [error, setError] = useState<Error | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!audioUrl) return

    const cacheKey = getCacheKey(audioUrl, options)
    const cachedThumbnail = thumbnailCache.get(cacheKey)

    if (cachedThumbnail) {
      setThumbnail(cachedThumbnail)
      return
    }

    const audio = new Audio()
    const canvas = document.createElement('canvas')
    const img = new Image()

    setLoading(true)

    const handleLoadedData = () => {
      try {
        // 设置 canvas 尺寸与默认缩略图一致
        canvas.width = img.width
        canvas.height = img.height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          throw new Error('无法获取 canvas context')
        }

        // 绘制默认缩略图
        ctx.drawImage(img, 0, 0, img.width, img.height)

        // 格式化音频时长
        const duration = audio.duration
        const durationText = formatDuration(duration)

        // 绘制时长文本
        ctx.font = '400 24px Inter'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillStyle = '#4B5563'
        ctx.fillText(durationText, img.width / 2, img.height - 16 * 3)

        // 转换为 base64
        const dataUrl = canvas.toDataURL(type, quality)
        // 存入缓存
        thumbnailCache.set(cacheKey, dataUrl)
        setThumbnail(dataUrl)
        setLoading(false)
      } catch (err) {
        setError(err instanceof Error ? err : new Error('生成缩略图失败'))
        setLoading(false)
        if (defaultThumbnail) {
          setThumbnail(defaultThumbnail)
        }
      }
    }

    const handleError = () => {
      setError(new Error('音频加载失败'))
      setLoading(false)
    }

    // 加载默认缩略图
    img.src = defaultThumbnail
    img.onload = () => {
      // 设置音频事件处理
      audio.addEventListener('loadeddata', handleLoadedData)
      audio.addEventListener('error', handleError)

      // 设置音频源
      audio.src = audioUrl
      audio.preload = 'metadata' // 只加载元数据以获取时长
    }

    // 清理函数
    return () => {
      audio.removeEventListener('loadeddata', handleLoadedData)
      audio.removeEventListener('error', handleError)
      audio.src = ''
      audio.remove()

      canvas.remove()
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
    }
  }, [audioUrl, options.quality, options.type, options.defaultThumbnail])

  return { thumbnail, loading, error }
}
